// deps
import React, { ReactElement, memo, useCallback, useState } from "react";

// internal-deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { isEmpty } from "@swiggy-private/js-utils";

// style components
import {
    TurboPodIconImage,
    TurboPodImageWrapper,
    Title,
    Subtitle,
    ConsentCtaSection,
    ConsentCtaWrapper,
    SubtleCtaSection,
    SubtleSecondaryCta,
} from "./styles";

// interfaces
import {
    TURBO_COD_CONFIRM_HALF_CARD_TYPE,
    CodConsentInfo,
    TurboCodPaymentMethodMeta,
    TurboPodHalfCardEvents,
} from "@payment-ui/interfaces";
import { getPodSubtleVariantWaitTimeInMs } from "@payment-ui/helpers/TurboPayment";
import { TURBO_POD_HALF_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

export interface PodConsentHalfCardProps {
    data: TurboCodPaymentMethodMeta;
    onPlaceOrder: VoidFunction;
    onCancel: VoidFunction;
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
    isActive: boolean;
}

export interface PodConsentHalfCardContentProps {
    iconUrl?: string;
    halfCard: TURBO_COD_CONFIRM_HALF_CARD_TYPE;
    halfCardInfo: CodConsentInfo;
    onPlaceOrder: VoidFunction;
    onCancel: VoidFunction;
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
}

const DEFAULT_ICON_URL = "";

const PodConsentHalfCardContent = (props: PodConsentHalfCardContentProps): ReactElement | null => {
    const { turboPodHalfCardEvents, onPlaceOrder, onCancel, iconUrl, halfCard, halfCardInfo } = props;
    const [subtleProgress, setSubtleProgress] = useState(true);

    useEffectOnce(() => {
        if (!isEmpty(halfCard) && !isEmpty(halfCardInfo)) {
            turboPodHalfCardEvents.onLoad(halfCard);
        }
    });

    const onPrimaryCtaClick = useCallback(() => {
        turboPodHalfCardEvents.onPlaceOrderCtaClick(halfCard);
        onPlaceOrder();
    }, [halfCard, onPlaceOrder, turboPodHalfCardEvents]);

    const onSecondaryCtaClick = useCallback(() => {
        turboPodHalfCardEvents.onCancelCtaClick(halfCard);
        onCancel();
    }, [halfCard, onCancel, turboPodHalfCardEvents]);

    const onSubtleVariantCompleteProgress = useCallback(() => {
        setSubtleProgress(false);
        turboPodHalfCardEvents.onPlaceOrderCtaClick(halfCard, "yes");
        onPlaceOrder();
    }, [halfCard, onPlaceOrder, turboPodHalfCardEvents]);

    const renderCtaSection = useCallback(() => {
        if (halfCard === TURBO_COD_CONFIRM_HALF_CARD_TYPE.CONSENT) {
            return (
                <ConsentCtaSection>
                    <ConsentCtaWrapper>
                        <SuperButton
                            isDisabled={false}
                            variant="fullwidth"
                            buttonType="tertiaryLow"
                            buttonSize="large"
                            onClick={onSecondaryCtaClick}
                            data-testid={TURBO_POD_HALF_CARD_TEST_IDS.SECONDARY_CTA}
                        >
                            {halfCardInfo.secondaryCta}
                        </SuperButton>
                    </ConsentCtaWrapper>
                    <ConsentCtaWrapper>
                        <SuperButton
                            isDisabled={false}
                            variant="fullwidth"
                            buttonType="specialPositive"
                            buttonSize="large"
                            onClick={onPrimaryCtaClick}
                            data-testid={TURBO_POD_HALF_CARD_TEST_IDS.PRIMARY_CTA}
                        >
                            {halfCardInfo.primaryCta}
                        </SuperButton>
                    </ConsentCtaWrapper>
                </ConsentCtaSection>
            );
        }
        const waitTimeInMs = getPodSubtleVariantWaitTimeInMs(halfCardInfo);

        return (
            <SubtleCtaSection>
                <SuperButton
                    isDisabled={false}
                    variant="fullwidth"
                    buttonType="specialPositive"
                    buttonSize="large"
                    onClick={onPrimaryCtaClick}
                    progressTime={waitTimeInMs}
                    showProgressBar={subtleProgress}
                    onCompleteProgress={onSubtleVariantCompleteProgress}
                    data-testid={TURBO_POD_HALF_CARD_TEST_IDS.PRIMARY_CTA}
                >
                    {halfCardInfo.primaryCta}
                </SuperButton>
                <SubtleSecondaryCta
                    buttonSize="medium"
                    color="text_High_Emphasis"
                    onClick={onSecondaryCtaClick}
                    data-testid={TURBO_POD_HALF_CARD_TEST_IDS.SECONDARY_CTA}
                >
                    {halfCardInfo.secondaryCta}
                </SubtleSecondaryCta>
            </SubtleCtaSection>
        );
    }, [
        halfCard,
        halfCardInfo,
        onPrimaryCtaClick,
        onSecondaryCtaClick,
        onSubtleVariantCompleteProgress,
        subtleProgress,
    ]);

    return (
        <>
            <TurboPodImageWrapper>
                <TurboPodIconImage cloudinaryId={iconUrl ?? DEFAULT_ICON_URL} />
            </TurboPodImageWrapper>
            <Title variant="V2.heading1.neutral.black" data-testid={TURBO_POD_HALF_CARD_TEST_IDS.TITLE}>
                {halfCardInfo.title}
            </Title>
            <Subtitle variant="V2.body2.neutral.regular" data-testid={TURBO_POD_HALF_CARD_TEST_IDS.SUB_TITLE}>
                {halfCardInfo.subtitle}
            </Subtitle>
            {renderCtaSection()}
        </>
    );
};

function PodConsentHalfCard(props: PodConsentHalfCardProps): ReactElement | null {
    const { isActive, data, ...restProps } = props;
    const { halfCard, halfCardInfo, iconUrl } = data;

    if (isEmpty(halfCard) || isEmpty(halfCardInfo)) {
        return null;
    }

    return (
        <HalfCard isOpen={isActive} hideCloseIcon closeOnOverlayClick={false}>
            <PodConsentHalfCardContent
                iconUrl={iconUrl}
                halfCard={halfCard}
                halfCardInfo={halfCardInfo}
                {...restProps}
            />
        </HalfCard>
    );
}

export default memo(PodConsentHalfCard);
